import React, { useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Alert,
  Form,
  InputGroup,
  Modal,
  Toast,
  ToastContainer,
  Spinner,
  Pagination,
} from 'react-bootstrap';
import {
  Prescription,
  Clock,
  CheckCircle,
  FileText,
  Eye,
  Search,
  Shield,
  People,
  GraphUp,
  InfoCircle,
  Check,
  PersonCircle,
  Activity,
  Download,
  Printer,
  ChevronDown,
  ChevronUp,
} from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/AuthContext';

// Données initiales (inchangées)
const initialPrescriptions = [
  { id: 'P001', date: '2025-03-12', patientName: 'Jean Dupont', patientId: 'CNAMGS-123456', medicines: 'Paracétamol 500mg, Vitamine C', dosage: '2x/jour', duration: '5 jours', status: 'pending', doctor: 'Dr. Nzogho' },
  { id: 'P002', date: '2025-03-10', patientName: 'Marie Nzogho', patientId: 'CNAMGS-789012', medicines: 'Cetirizine 10mg', dosage: '1x/jour', duration: '7 jours', status: 'pending', doctor: 'Dr. Ondo' },
  { id: 'P003', date: '2025-03-08', patientName: 'Pierre Mba', patientId: 'CNAMGS-345678', medicines: 'Amoxicilline 500mg', dosage: '2x/jour', duration: '7 jours', status: 'pending', doctor: 'Dr. Nzogho' },
  { id: 'P004', date: '2025-03-05', patientName: 'Sophie Nguema', patientId: 'CNAMGS-111222', medicines: 'Loratadine 10mg', dosage: '1x/jour', duration: '10 jours', status: 'pending', doctor: 'Dr. Ondo' },
];

const initialReimbursements = [
  { id: 'R001', date: '2025-03-12', patientName: 'Jean Dupont', patientId: 'CNAMGS-123456', amount: 15000, status: 'pending', prescriptionId: 'P001' },
  { id: 'R002', date: '2025-03-10', patientName: 'Marie Nzogho', patientId: 'CNAMGS-789012', amount: 8000, status: 'pending', prescriptionId: 'P002' },
  { id: 'R003', date: '2025-03-08', patientName: 'Pierre Mba', patientId: 'CNAMGS-345678', amount: 12000, status: 'pending', prescriptionId: 'P003' },
];

const PharmacistDashboard = () => {
  const { user } = useAuth();
  const [prescriptions, setPrescriptions] = useState(initialPrescriptions);
  const [reimbursements, setReimbursements] = useState(initialReimbursements);
  const [searchPrescription, setSearchPrescription] = useState('');
  const [searchReimbursement, setSearchReimbursement] = useState('');
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showReimbursementModal, setShowReimbursementModal] = useState(false);
  const [selectedReimbursement, setSelectedReimbursement] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [dispensing, setDispensing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Pagination
  const [prescCurrentPage, setPrescCurrentPage] = useState(1);
  const [prescItemsPerPage, setPrescItemsPerPage] = useState(5);
  const [reimbCurrentPage, setReimbCurrentPage] = useState(1);
  const [reimbItemsPerPage, setReimbItemsPerPage] = useState(5);

  // État pour les lignes déroulantes (accordéon) sur mobile
  const [expandedReimbursements, setExpandedReimbursements] = useState(new Set());

  const toggleReimbursementRow = (id) => {
    const newSet = new Set(expandedReimbursements);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedReimbursements(newSet);
  };

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };

  // Ordonnances en attente (commenté dans le JSX, mais on garde la logique)
  const pendingPrescriptions = useMemo(() => {
    let filtered = prescriptions.filter(p => p.status === 'pending');
    if (searchPrescription) {
      const term = searchPrescription.toLowerCase();
      filtered = filtered.filter(p =>
        p.patientName.toLowerCase().includes(term) ||
        p.id.toLowerCase().includes(term) ||
        p.medicines.toLowerCase().includes(term)
      );
    }
    return filtered;
  }, [prescriptions, searchPrescription]);

  const totalPrescPages = Math.ceil(pendingPrescriptions.length / prescItemsPerPage);
  const paginatedPrescriptions = useMemo(() => {
    const start = (prescCurrentPage - 1) * prescItemsPerPage;
    return pendingPrescriptions.slice(start, start + prescItemsPerPage);
  }, [pendingPrescriptions, prescCurrentPage, prescItemsPerPage]);

  // Remboursements en attente
  const pendingReimbursements = useMemo(() => {
    let filtered = reimbursements.filter(r => r.status === 'pending');
    if (searchReimbursement) {
      const term = searchReimbursement.toLowerCase();
      filtered = filtered.filter(r =>
        r.patientName.toLowerCase().includes(term) ||
        r.id.toLowerCase().includes(term)
      );
    }
    return filtered;
  }, [reimbursements, searchReimbursement]);

  const totalReimbPages = Math.ceil(pendingReimbursements.length / reimbItemsPerPage);
  const paginatedReimbursements = useMemo(() => {
    const start = (reimbCurrentPage - 1) * reimbItemsPerPage;
    return pendingReimbursements.slice(start, start + reimbItemsPerPage);
  }, [pendingReimbursements, reimbCurrentPage, reimbItemsPerPage]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const getStatusBadge = (status) => {
    if (status === 'pending') return <Badge bg="warning" className="rounded-pill px-3 py-2"><Clock size={12} className="me-1" /> En attente</Badge>;
    if (status === 'dispensed') return <Badge bg="success" className="rounded-pill px-3 py-2"><CheckCircle size={12} className="me-1" /> Délivrée</Badge>;
    if (status === 'submitted') return <Badge bg="info" className="rounded-pill px-3 py-2"><CheckCircle size={12} className="me-1" /> Soumise</Badge>;
    return <Badge bg="secondary">{status}</Badge>;
  };

  const handleDispense = (prescription) => {
    setSelectedPrescription(prescription);
    setShowDetailsModal(true);
  };

  const confirmDispense = () => {
    setDispensing(true);
    setTimeout(() => {
      setPrescriptions(prev =>
        prev.map(p =>
          p.id === selectedPrescription.id ? { ...p, status: 'dispensed' } : p
        )
      );
      showToast(`Ordonnance ${selectedPrescription.id} délivrée avec succès`, 'success');
      setShowDetailsModal(false);
      setSelectedPrescription(null);
      setDispensing(false);
    }, 800);
  };

  const handleReimbursementAction = (reimbursement) => {
    setSelectedReimbursement(reimbursement);
    setShowReimbursementModal(true);
  };

  const submitReimbursement = () => {
    setSubmitting(true);
    setTimeout(() => {
      setReimbursements(prev =>
        prev.map(r =>
          r.id === selectedReimbursement.id ? { ...r, status: 'submitted' } : r
        )
      );
      showToast(`Demande de remboursement ${selectedReimbursement.id} soumise`, 'success');
      setShowReimbursementModal(false);
      setSelectedReimbursement(null);
      setSubmitting(false);
    }, 800);
  };

  // Statistiques
  const stats = {
    pendingPrescriptions: prescriptions.filter(p => p.status === 'pending').length,
    pendingReimbursements: reimbursements.filter(r => r.status === 'pending').length,
    totalDispensed: prescriptions.filter(p => p.status === 'dispensed').length,
    satisfactionRate: 96,
  };

  const statsCards = [
    {
      title: 'Ordon. en attente',
      value: stats.pendingPrescriptions,
      icon: Prescription,
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      bg: 'white',
    },
    {
      title: 'D. remboursement',
      value: stats.pendingReimbursements,
      icon: FileText,
      color: '#4361ee',
      gradient: 'linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)',
      bg: 'white',
    },
    {
      title: 'Ordon. délivrées',
      value: stats.totalDispensed,
      icon: CheckCircle,
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      bg: 'white',
    },
    {
      title: 'Satisfact. client',
      value: `${stats.satisfactionRate}%`,
      icon: GraphUp,
      color: '#06b6d4',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      bg: 'white',
    },
  ];

  const renderPagination = (current, total, onChange) => {
    if (total <= 1) return null;
    const items = [];
    for (let i = 1; i <= total; i++) {
      items.push(
        <Pagination.Item key={i} active={i === current} onClick={() => onChange(i)}>
          {i}
        </Pagination.Item>
      );
    }
    return (
      <div className="d-flex justify-content-end mt-3">
        <Pagination size="sm">
          <Pagination.Prev onClick={() => onChange(Math.max(1, current - 1))} disabled={current === 1} />
          {items}
          <Pagination.Next onClick={() => onChange(Math.min(total, current + 1))} disabled={current === total} />
        </Pagination>
      </div>
    );
  };

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        <Toast show={toast.show} onClose={() => setToast(prev => ({ ...prev, show: false }))} bg={toast.variant} delay={4000} autohide>
          <Toast.Header closeButton={false}>
            <strong className="me-auto">
              {toast.variant === 'success' ? 'Succès' : toast.variant === 'danger' ? 'Erreur' : 'Information'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="container-fluid">
        {/* En-tête */}
        <div className="welcome-section mb-4 p-4 rounded-4" style={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white',
        }}>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-circle p-3">
                  <PersonCircle size={48} style={{ color: '#1e3c72' }} />
                </div>
                <div>
                  <h1 className="h3 fw-bold mb-1">Bonjour, {user?.name?.split(' ')[0] || 'Pharmacien'} !</h1>
                  <p className="mb-0 opacity-90">Voici le résumé de votre activité.</p>
                  <div className="mt-2">
                    <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
                      <Activity className="me-1" size={14} />
                      3 ordonnances aujourd'hui
                    </Badge>
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Button variant="light" className="rounded-pill px-4 me-2">
                <Download size={16} className="me-2" /> Exporter
              </Button>
              <Button variant="outline-light" className="rounded-pill px-4">
                <Printer size={16} className="me-2" /> Imprimer
              </Button>
            </Col>
          </Row>
        </div>

        {/* Cartes de statistiques */}
        <Row className="g-3 mb-4">
          {statsCards.map((card, idx) => (
            <Col md={6} lg={3} xs={6} key={idx}>
              <Card className="border-0 shadow-sm stats-card h-100 bg-white" style={{ borderRadius: '16px', background: card.bg }}>
                <div className="card-gradient" style={{ background: card.gradient, height: '4px' }}></div>
                <Card.Body className="p-3 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="text-muted mb-1 small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                        {card.title}
                      </h6>
                      <h3 className="mb-0 fw-bold" style={{ fontSize: '1.8rem' }}>
                        {card.value}
                      </h3>
                    </div>
                    <div className="icon-container rounded-3 d-flex align-items-center justify-content-center" style={{ background: `${card.color}20`, width: '48px', height: '48px' }}>
                      <card.icon size={28} style={{ color: card.color }} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Section Demandes de remboursement (avec adaptation mobile) */}
        <Row>
          <Col md={12}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                    <FileText size={18} className="text-primary" />
                    Demandes de remboursement
                  </h5>
                  <InputGroup style={{ width: '300px' }}>
                    <InputGroup.Text className="bg-light border-0">
                      <Search size={14} />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Rechercher patient..."
                      value={searchReimbursement}
                      onChange={(e) => setSearchReimbursement(e.target.value)}
                      className="border-0 bg-light"
                    />
                  </InputGroup>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th>Date</th>
                        <th>Patient</th>
                        <th className="d-none d-sm-table-cell">Montant</th>
                        <th className="d-none d-sm-table-cell">Statut</th>
                        <th className="d-none d-sm-table-cell">Actions</th>
                        <th className="d-sm-none">Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedReimbursements.length > 0 ? (
                        paginatedReimbursements.map(r => {
                          const isExpanded = expandedReimbursements.has(r.id);
                          return (
                            <React.Fragment key={r.id}>
                              {/* Ligne principale */}
                              <tr className="align-middle">
                                <td data-label="Date" className="small">{formatDate(r.date)}</td>
                                <td data-label="Patient" className="fw-medium">{r.patientName}</td>
                                <td data-label="Montant" className="fw-bold text-success d-none d-sm-table-cell">{formatCurrency(r.amount)}</td>
                                <td data-label="Statut" className="d-none d-sm-table-cell">{getStatusBadge(r.status)}</td>
                                <td data-label="Actions" className="d-none d-sm-table-cell">
                                  <Button
                                    size="sm"
                                    variant="outline-success"
                                    className="rounded-pill px-3"
                                    onClick={() => handleReimbursementAction(r)}
                                  >
                                    <Check size={14} className="me-1" /> Soumettre
                                  </Button>
                                </td>
                                <td className="d-sm-none text-center" style={{ verticalAlign: 'middle' }}>
                                  <Button
                                    variant="link"
                                    onClick={() => toggleReimbursementRow(r.id)}
                                    className="p-0 text-secondary"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                  </Button>
                                </td>
                              </tr>
                              {/* Ligne de détails (visible seulement sur mobile et si dépliée) */}
                              {isExpanded && (
                                <tr className="d-sm-none detail-row">
                                  <td colSpan="6" className="p-3 bg-light">
                                    <div className="row g-2">
                                      <div className="col-6"><strong>Montant :</strong> {formatCurrency(r.amount)}</div>
                                      <div className="col-6"><strong>Statut :</strong> {getStatusBadge(r.status)}</div>
                                      <div className="col-12 mt-2">
                                        <Button
                                          size="sm"
                                          variant="outline-success"
                                          className="rounded-pill"
                                          onClick={() => handleReimbursementAction(r)}
                                        >
                                          <Check size={14} className="me-1" /> Soumettre
                                        </Button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <FileText size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucune demande de remboursement</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
                {renderPagination(reimbCurrentPage, totalReimbPages, setReimbCurrentPage)}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Information */}
        <Row className="mt-4">
          <Col md={12}>
            <Alert variant="info" className="border-0 rounded-3 shadow-sm" style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #d9eaff 100%)' }}>
              <div className="d-flex align-items-center gap-3">
                <InfoCircle size={24} className="text-info" />
                <div>
                  <strong className="d-block mb-1">Suivi des prescriptions</strong>
                  <span className="small">
                    Les ordonnances doivent être vérifiées et délivrées. Les demandes de remboursement sont automatiquement générées après délivrance.
                  </span>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        {/* Modals (inchangés) */}
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered>
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title className="d-flex align-items-center gap-2">
              <Prescription size={20} />
              Délivrance de l'ordonnance
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedPrescription && (
              <>
                <div className="mb-3 p-3 rounded-3" style={{ background: '#e8f5e9', borderLeft: '4px solid #10b981' }}>
                  <div className="d-flex align-items-center gap-2">
                    <People size={18} className="text-success" />
                    <strong>Patient :</strong> {selectedPrescription.patientName}
                    <span className="text-muted">({selectedPrescription.patientId})</span>
                  </div>
                </div>
                <Row>
                  <Col md={6}>
                    <div className="mb-3"><small className="text-muted d-block">Médecin prescripteur</small><strong>{selectedPrescription.doctor}</strong></div>
                    <div className="mb-3"><small className="text-muted d-block">Date</small><strong>{formatDate(selectedPrescription.date)}</strong></div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3"><small className="text-muted d-block">Médicaments</small><strong>{selectedPrescription.medicines}</strong></div>
                    <div className="mb-3"><small className="text-muted d-block">Posologie</small><strong>{selectedPrescription.dosage}</strong></div>
                  </Col>
                </Row>
                <Alert variant="info" className="mt-2"><InfoCircle size={14} className="me-1" /> Après délivrance, la demande de remboursement sera automatiquement créée.</Alert>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDetailsModal(false)} className="rounded-pill">Annuler</Button>
            <Button variant="success" onClick={confirmDispense} disabled={dispensing} className="rounded-pill px-4">
              {dispensing ? <Spinner size="sm" className="me-1" /> : <CheckCircle size={16} className="me-1" />}
              {dispensing ? 'Délivrance...' : 'Délivrer'}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showReimbursementModal} onHide={() => setShowReimbursementModal(false)} size="md" centered>
          <Modal.Header closeButton className="bg-success text-white">
            <Modal.Title className="d-flex align-items-center gap-2">
              <FileText size={20} />
              Soumettre une demande de remboursement
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedReimbursement && (
              <>
                <div className="mb-3 p-3 rounded-3" style={{ background: '#e8f5e9', borderLeft: '4px solid #10b981' }}>
                  <div className="d-flex align-items-center gap-2">
                    <People size={18} className="text-success" />
                    <strong>Patient :</strong> {selectedReimbursement.patientName}
                    <span className="text-muted">({selectedReimbursement.patientId})</span>
                  </div>
                </div>
                <Row>
                  <Col md={6}><div className="mb-3"><small className="text-muted d-block">Date</small><strong>{formatDate(selectedReimbursement.date)}</strong></div></Col>
                  <Col md={6}><div className="mb-3"><small className="text-muted d-block">Montant</small><strong className="text-success">{formatCurrency(selectedReimbursement.amount)}</strong></div></Col>
                </Row>
                <Alert variant="info" className="mt-2"><InfoCircle size={14} className="me-1" /> La demande sera envoyée à la CNAMGS pour traitement.</Alert>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowReimbursementModal(false)} className="rounded-pill">Annuler</Button>
            <Button variant="success" onClick={submitReimbursement} disabled={submitting} className="rounded-pill px-4">
              {submitting ? <Spinner size="sm" className="me-1" /> : <Check size={16} className="me-1" />}
              {submitting ? 'Soumission...' : 'Soumettre'}
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 16px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
          }
          .card-gradient {
            transition: height 0.2s ease;
          }
          .stats-card:hover .card-gradient {
            height: 6px;
          }
          .icon-container {
            transition: transform 0.2s ease;
          }
          .stats-card:hover .icon-container {
            transform: scale(1.05);
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
            vertical-align: middle;
          }
          @media (max-width: 768px) {
            .table td, .table th {
              padding: 0.5rem;
            }
            .btn-sm {
              padding: 0.25rem 0.5rem;
              font-size: 0.75rem;
            }
            .detail-row td {
              padding: 0.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default PharmacistDashboard;